import React, { useCallback, useEffect, memo, type MouseEvent, type ElementRef } from 'react';
import uuid from 'uuid/v4';
import { Checkbox } from '@atlaskit/checkbox';
import VisuallyHidden from '@atlaskit/visually-hidden';
import { BULK_SELECT_CHECKBOX_ID } from '../../constants';
import type { CellNavigationProps } from '../../types/cell-navigation';

type CheckboxProp = {
	id?: string;
	onClick: (props: { isChecked: boolean; shiftKeyPressed: boolean }) => void;
	label: string;
	isChecked: boolean;
	isIndeterminate?: boolean;
	isDisabled?: boolean;
} & CellNavigationProps;

export const BulkSelectCheckbox = memo<CheckboxProp>(
	({
		id,
		onClick,
		isChecked,
		isIndeterminate,
		isSelected, // cellNavigation prop
		wrapperRef,
		onKeyDown,
		isDisabled,
		label,
		...props
	}: CheckboxProp) => {
		const bulkSelectId = id ?? `${BULK_SELECT_CHECKBOX_ID}_${uuid()}`;
		const handleOnClick = useCallback(
			(event: MouseEvent<HTMLInputElement>) => {
				onClick({
					isChecked: event?.currentTarget?.checked,
					shiftKeyPressed: !!event?.shiftKey,
				});
			},
			[onClick],
		);

		useEffect(() => {
			if (isSelected && wrapperRef?.current) {
				wrapperRef.current.focus();
			}
		}, [isSelected, wrapperRef]);

		const onWrapperRef = useCallback(
			(ref: ElementRef<'input'> | null) => {
				if (wrapperRef) {
					// eslint-disable-next-line no-param-reassign
					wrapperRef.current = ref;
				}
			},
			[wrapperRef],
		);

		return (
			<>
				<VisuallyHidden>
					<label htmlFor={bulkSelectId}>{label}</label>
				</VisuallyHidden>
				<Checkbox
					id={bulkSelectId}
					onClick={handleOnClick}
					isChecked={isChecked}
					isDisabled={isDisabled}
					isIndeterminate={isIndeterminate}
					testId="business-list.common.ui.bulk-select-checkbox"
					onKeyDown={onKeyDown}
					{...(wrapperRef ? { ref: onWrapperRef } : {})}
					{...props}
				/>
			</>
		);
	},
);
